import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { formatDMY } from "@/lib/dateFormat";
import { toast } from "sonner";

const FALTA = {
  programa: "el grupo no tiene programa asignado",
  precio: "el programa no tiene precio",
  "sesiones por ciclo": "el programa no tiene clases por ciclo",
};

const Fila = ({ r, busy, onFix }) => (
  <div data-testid={`billing-anomaly-${r.enrollment_id}`}
       className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white border border-amber-200 px-3 py-2">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900 truncate">{r.student_name}</p>
      <p className="text-xs text-slate-500 truncate">
        {[r.group_name, r.program_name].filter(Boolean).join(" · ") || "sin grupo ni programa"}
        {r.start_date ? ` · desde ${formatDMY(r.start_date)}` : ""}
      </p>
      {r.actionable ? (
        <p className="text-xs text-amber-700 mt-0.5">
          Lleva {r.consumed} de {r.total} clases del ciclo #{r.cycle_number} · el cobro saldría por ${r.suggested_amount}
        </p>
      ) : (
        <p className="text-xs text-slate-500 mt-0.5">
          Antes de cobrar hay que arreglar: {r.missing.map((m) => FALTA[m] || m).join(" y ")}
        </p>
      )}
    </div>
    {r.actionable && (
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onFix(r, false)}
                data-testid={`billing-anomaly-fix-${r.enrollment_id}`}>
          Solo generar el cobro
        </Button>
        <Button size="sm" disabled={busy} onClick={() => onFix(r, true)}
                data-testid={`billing-anomaly-fix-notify-${r.enrollment_id}`}>
          Generar y enviarle el enlace
        </Button>
      </div>
    )}
  </div>
);

/** Matrículas activas cuyo ciclo en curso no tiene cobro: sin ese cobro el ciclo nunca
 *  cierra, así que no nace la factura del ciclo siguiente ni sale el enlace de pago. */
export const BillingAnomalies = ({ onFixed }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/billing/anomalies");
      setItems(r.data.items || []);
    } catch { /* sin permiso: no se muestra */ }
  };
  useEffect(() => { load(); }, []);

  const fix = async (row, notify) => {
    setBusy(row.enrollment_id);
    try {
      const r = await api.post(`/billing/anomalies/${row.enrollment_id}/fix`, { notify });
      toast.success(r.data.email_sent
        ? `Cobro creado y enlace enviado a ${row.student_name}`
        : `Cobro creado para ${row.student_name}${notify ? " (el correo no salió)" : ""}`);
      await load();
      onFixed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el cobro");
    } finally {
      setBusy(null);
    }
  };

  if (!items.length) return null;
  const listas = items.filter((r) => r.actionable);
  const trabadas = items.filter((r) => !r.actionable);

  return (
    <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50/70 overflow-hidden"
         data-testid="billing-anomalies-card">
      <button onClick={() => setOpen((v) => !v)} data-testid="billing-anomalies-toggle"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-100/60 transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          {listas.length > 0
            ? `${listas.length} ${listas.length === 1 ? "estudiante está tomando clases sin cobro" : "estudiantes están tomando clases sin cobro"}`
            : `${trabadas.length} matrículas sin cobro que necesitan un ajuste`}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-amber-700" /> : <ChevronDown className="h-4 w-4 text-amber-700" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-amber-800 mb-3">
            Sin el cobro del ciclo en curso el sistema nunca lo cierra: no se emite la factura del
            ciclo siguiente ni le llega el enlace de pago al estudiante.
          </p>
          {listas.length > 0 && (
            <div className="space-y-2" data-testid="billing-anomalies-ready">
              {listas.map((r) => (
                <Fila key={r.enrollment_id} r={r} busy={busy === r.enrollment_id} onFix={fix} />
              ))}
            </div>
          )}
          {trabadas.length > 0 && (
            <>
              <p className="text-xs font-semibold text-slate-600 mt-4 mb-2">
                Estas {trabadas.length} no se pueden cobrar todavía (les falta un dato del programa):
              </p>
              <div className="space-y-2" data-testid="billing-anomalies-blocked">
                {trabadas.map((r) => (
                  <Fila key={r.enrollment_id} r={r} busy={false} onFix={fix} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
